import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Grid from "@mui/material/Grid";
import { Form } from "semantic-ui-react";
import {
  COLOR_MILANO_RED
} from "../../../../constants/styles";
import { Typography } from "../../../../UI/Typography";
import { Select } from "../../../../UI/Select";
import { getRawDateFromDayjs } from "../../../../common/date_utils";
import { getDefaultDate } from "../../../../helpers/getDefaultDate";
import { L2MultiSelector } from "../../../../common/network/NetworkSelectors";
import { MergeL2, L2MergeRequest, MatchingRoundObject } from "../../../../services/NetworkApi";
import { useNetworkContext } from "../../../../contexts/NetworkContext/context";
import { MatchingDatesPartial } from "../../../../components/MatchingDatesPartial";

import useAuth from "../../../login/AuthContext";
interface IMergeSubnetworkModal {
  show: boolean;
  onClose: () => void;
  l1Id: number;
}

interface MergeSelectOption {
  label: string;
  value: string;
  status: string;
}

const CUSTOM_L2_SELECTOR_LABEL = "Previous Matching Rounds to copy (all Unit Types and settings will be added to new round.";
const CUSTOM_COPY_L2_SELECTOR_LABEL = "Which Matching Round settings will be source to copy from?";

export const MergeSubnetworkModal: React.FC<IMergeSubnetworkModal> = ({
  show,
  onClose,
  l1Id,
  
}) => {
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const { getAuthToken } = useAuth();
  const [matchingEndDate, setMatchingEndDate] = useState<string>();

  const defaultErrors = {
    subnetworkNameError: false,
    matchingDatesError: false,
    matchingRoundsToBeMergedError: false,
    settingsSourceError: false,
    closedArchivedError: false
  };

  const defaultData = {
    name: "",
    matchingRoundsToBeMerged: [],
    settingsSource: null,
    matchingStartDate: null,
    matchingEndDate: null,
    activationStartDate: null,
    activationEndDate: null
  };

  const [requestFields, setRequestFields] = useState<L2MergeRequest>(defaultData);
  const [settingsSourceList, setSettingsSourceList] = useState<Array<MergeSelectOption>>([]);
  const [errors, setErrors] = useState(defaultErrors);

  const resetData = () => {
    setRequestFields(defaultData);
    setSettingsSourceList([]);
    setErrors(defaultErrors);
    setMatchingEndDate("");
  };

  const validateFields = () => {
    const validationErrors = { ...defaultErrors };

    let errored = false;

    validationErrors.subnetworkNameError = requestFields.name == "";
    errored = errored || requestFields.name == "";

    validationErrors.matchingRoundsToBeMergedError = requestFields.matchingRoundsToBeMerged.length === 0;
    errored = errored || requestFields.matchingRoundsToBeMerged.length === 0;

    validationErrors.settingsSourceError = requestFields.settingsSource === null;
    errored = errored || requestFields.settingsSource === null;

    const isEveryDateIsSet = (requestFields.matchingStartDate !== null && requestFields.matchingEndDate !== null && requestFields.activationStartDate !== null && requestFields.activationEndDate !== null);
    const isEveryDateIsNull = (requestFields.matchingStartDate === null && requestFields.matchingEndDate === null && requestFields.activationStartDate === null && requestFields.activationEndDate === null);
    
    if(isEveryDateIsSet === false && isEveryDateIsNull === false) {
      validationErrors.matchingDatesError = true;
    }
    errored = errored || validationErrors.matchingDatesError;
    
    validationErrors.closedArchivedError =  requestFields.matchingEndDate? !!getDefaultDate(matchingEndDate)?.isBefore(new Date()) : false;
    errored = errored || validationErrors.closedArchivedError;

    setErrors(validationErrors);
    return !errored;
  };

  const setDateValues = (value: string | boolean | null, field: string) => {
    if(value) {
      const daysDate = getDefaultDate(value as string);
      setRequestFields({...requestFields, [field]: getRawDateFromDayjs(daysDate) });
    }
    if(value && field === "matchingEndDate") {
      setMatchingEndDate(value as string);
    }
    
  };

  const setMatchingRoundsToBeMerged = (value: MergeSelectOption[]) => {
    if(value) {
      setSettingsSourceList(value);
      const matchingRoundsToBeMerged: any = value.map((option) => ({
        l1Id,
        l2Id: option.value
      }));
      setRequestFields({...requestFields, matchingRoundsToBeMerged: matchingRoundsToBeMerged as  MatchingRoundObject[]});
    }
  };

  const setMatchingRoundSource = (value: any) => {
    setRequestFields({...requestFields, settingsSource: {
       l1Id: String(l1Id), l2Id: value.value}
    });
  };

  const onSubmit = () => {
    if (validateFields()) {
      const merge = async () => {
        await MergeL2(requestFields, l1Id, await getAuthToken());
  
        await loadNetworkInfoForNetworks([l1Id]);
        onClose();
      };
      merge();
    }
  };


  return (
    <Modal show={show} onHide={onClose} onExiting={resetData}>
      <Modal.Header closeButton>
        <Modal.Title>Create New Matching Round from Existing </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          error={errors.subnetworkNameError}
          onSubmit={onSubmit}
        >
          <Form.Field error={errors.subnetworkNameError}>
            <label>Matching Round Name</label>
            <input
              onChange={(event) => {
                setRequestFields({
                  ...requestFields,
                  name: event.target.value,
                });
              }}
            />
          </Form.Field>
          <MatchingDatesPartial
            matchingDatesError={errors.matchingDatesError}
            closedArchivedError={errors.closedArchivedError}
            setValue={setDateValues}
          />
          <Grid container gap={3}>
            <L2MultiSelector 
              onSelectHandler={setMatchingRoundsToBeMerged} 
              isMulti={true} 
              fullWidth
              withStatus={false}
              customLabel={CUSTOM_L2_SELECTOR_LABEL}
            />
          </Grid>
          { 
            errors.matchingRoundsToBeMergedError &&
            <Grid container gap={3}>
              <Typography color={COLOR_MILANO_RED}>Please set at least one Matching Round.</Typography>
            </Grid>
          }
          <Grid container gap={3} pt={2}>
            <Select
              fullWidth
              onChange={setMatchingRoundSource}
              options={settingsSourceList}
              label={CUSTOM_COPY_L2_SELECTOR_LABEL}
            />
          </Grid>
          { 
            errors.settingsSourceError &&
            <Grid container gap={3}>
              <Typography color={COLOR_MILANO_RED}>Please set a matching round that will be a source of the settings.</Typography>
            </Grid>
          }
          <Button style={{ marginTop: "5px" }} type="submit">
            Merge
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
